import { Packaging, Product, ProductPackaging } from "src/api/client-api/products/useGetOneProduct";
import { CompletionMetadata, Completions, CompletionService } from "../../CompletionService";
import { PackagingPostListQuery } from "src/api/client-api/post/useGetPackagingsPosts";

type EntityId = number;
export type PackagingsCompletionsMetadata = Record<EntityId, CompletionMetadata>;

const hasPackaging = (
    productPackaging: ProductPackaging,
): productPackaging is ProductPackaging & { packaging: Packaging } =>
    productPackaging.packaging !== undefined && productPackaging.packaging !== null;

type ConstructorOptions = {
    product: Product | undefined;
    packagingPosts: PackagingPostListQuery[];
};
export class ProductPackagingCompletionService extends CompletionService {
    private packagingsMetadata: PackagingsCompletionsMetadata = {};
    private readonly packagings: Packaging[];
    private readonly packagingPosts: PackagingPostListQuery[];

    public constructor(options: ConstructorOptions) {
        super();
        const { product, packagingPosts } = options;

        this.packagingPosts = packagingPosts;

        const packagings = product?.productPackagings?.filter(hasPackaging).map((pPack) => pPack.packaging) ?? [];
        this.packagings = packagings;

        this.computePackagingsMetadata();
    }

    public getMetadata(): PackagingsCompletionsMetadata {
        return this.packagingsMetadata;
    }

    private computePackagingsMetadata() {
        const initialAcc: PackagingsCompletionsMetadata = {};
        const metadata = this.packagings.reduce((acc, packaging) => {
            const packagingPosts = this.packagingPosts.find((p) => p?.packagingId === packaging.id)?.posts ?? [];

            const hasProgress = packagingPosts.length > 0;

            // I let it here because `isValid` was used for Ingredient
            // saying "I have a questionnaireLink in the reference"
            const isValid = packagingPosts.length > 0;

            const ingredientMetadata: CompletionMetadata = {
                isValid,
                hasProgress,
                reference: {
                    score: undefined,
                    label: undefined,
                },
                posts: packagingPosts,
            };
            acc[packaging.id] = ingredientMetadata;
            return acc;
        }, initialAcc);

        this.packagingsMetadata = metadata;
    }

    public getCompletions(): Completions {
        const emptyCompletion = this.getEmptyCompletions();

        let grandTotalAverage = 0;
        const ingredientCompletions = this.packagings.reduce((acc, packaging) => {
            const metadata = this.packagingsMetadata[packaging.id];
            const completion = this.getPostListCompletion(metadata.posts);

            acc.byEntityId[packaging.id] = completion;

            acc.totalAverageItems += completion.totalAverageItems;
            acc.completedCount += completion.completedCount;
            acc.count += completion.count;
            grandTotalAverage += completion.weightedAverage * completion.totalAverageItems;

            return acc;
        }, emptyCompletion);

        if (ingredientCompletions.totalAverageItems > 0) {
            ingredientCompletions.weightedAverage = grandTotalAverage / ingredientCompletions.totalAverageItems;
        }

        return ingredientCompletions;
    }
}
